"use client";

import { useEffect, useRef, useState } from "react";
import type { SecurityRef } from "@/lib/data/types";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
// Einmaliger Retry-Abstand bei HTTP 429 (siehe attempt() unten) - das Zeitfenster
// (SEARCH_WINDOW_MS in app/api/search/route.ts) ist 60s, aber ein einzelner Nutzer, der
// selbst gerade das Limit gerissen hat, hat es typischerweise nur knapp gerissen - eine
// kurze Pause reicht in der Praxis, ohne dass die Suche fuer eine volle Minute "kaputt"
// wirkt.
const RATE_LIMIT_RETRY_DELAY_MS = 1500;

/**
 * Gemeinsamer Debounce-Fetch-Hook fuer /api/search - loest zwei eigenstaendige Kopien
 * desselben Musters ab (components/search/search-hero.tsx, components/portfolio/
 * ticker-combobox.tsx), die beide denselben Bug hatten (backlog.md -> "Bug: Suche bleibt
 * nach der ersten Eingabe auf dem ersten Treffer haengen", Root-Cause 2026-08-17 per
 * Recherche-Agent geklaert, reproduziert auch als Eriks "keine Aktie wird vorgeschlagen"-
 * Report vom selben Tag).
 *
 * Race Condition: beide Kopien verliessen sich AUSSCHLIESSLICH auf `AbortController`, um
 * zu verhindern, dass eine aeltere, langsamere Response eine neuere ueberschreibt. Gewinnt
 * der Abort das Rennen gegen eine bereits im Flug befindliche Response nicht (plausibel bei
 * unterschiedlicher Kaltstart-Latenz je Serverless-Instanz), ueberschreibt die alte Antwort
 * die neue - und bleibt bestehen, da `suggestions` beim Start einer neuen Query nie aktiv
 * zurueckgesetzt wurde. Fix hier: zusaetzlich zum Abort wird die Query, zu der eine Response
 * gehoert, gegen die BEIM AUFLOESEN aktuelle Query geprueft (per Ref statt Closure-Wert,
 * damit der Vergleich nicht die zum Fetch-Start "eingefrorene" Query nutzt) - im Mismatch-
 * Fall wird die Response verworfen statt uebernommen.
 *
 * Rate-Limit-Haertung (Erik 2026-08-20, "Aktien-Suche": leere Trefferliste bei "johnson &
 * johnson" trotz vorhandener Treffer UND eine gleichbleibende alte Trefferliste bei
 * zwei weiteren, unabhaengig voneinander getippten Suchen ohne Klick dazwischen) - beide
 * Symptome liessen sich trotz ausfuehrlicher Live-Nachstellung (Playwright, verschiedene
 * Tipp-/Pausenmuster) nicht deterministisch reproduzieren, `/api/search` selbst lieferte in
 * jedem Test korrekte Treffer. Ein `HTTP 429` (Rate-Limit, `app/api/search/route.ts`) wird
 * hier bisher IDENTISCH zu "0 echte Treffer" behandelt (`!res.ok` -> `setSuggestions([])`),
 * ohne jede Fehleranzeige - bei einer intensiven Testsitzung mit vielen kurz aufeinander
 * getippten Suchen (jede Tipppause = ein Request) plausibel erreichbar und würde exakt wie
 * "die Suche antwortet nicht mehr" wirken. Ein einzelner automatischer Retry nach kurzer
 * Pause macht das selbstheilend, unabhaengig davon, ob dies Eriks tatsaechliche Ursache war.
 */
export function useDebouncedSearch(query: string): SecurityRef[] {
  const [suggestions, setSuggestions] = useState<SecurityRef[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const latestQueryRef = useRef(query);

  // Refs duerfen nicht waehrend des Renders geschrieben werden (react-hooks/refs) - eigener
  // Effect statt einer direkten Zuweisung im Funktionskoerper. Laeuft VOR dem Debounce-Effect
  // unten (beide haengen von `query` ab, React fuehrt Effects in Deklarationsreihenfolge aus),
  // der Ref ist also garantiert aktuell, bevor ein neuer Fetch-Timeout aufgesetzt wird.
  useEffect(() => {
    latestQueryRef.current = query;
  }, [query]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      // setState nicht synchron im Effect-Body (react-hooks/set-state-in-effect).
      const id = setTimeout(() => setSuggestions([]), 0);
      return () => clearTimeout(id);
    }

    const timeout = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      async function attempt(retriesLeft: number): Promise<void> {
        try {
          const res = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}`, {
            signal: controller.signal,
          });
          // Zwischenzeitlich weitergetippt? Diese Antwort gehoert dann zu einer bereits
          // veralteten Query - nicht mehr uebernehmen, selbst wenn ihr Abort das Rennen
          // gegen die Response nicht gewonnen hat (siehe Docstring oben).
          if (latestQueryRef.current.trim() !== trimmed) return;
          if (res.status === 429 && retriesLeft > 0) {
            await new Promise((resolve) => setTimeout(resolve, RATE_LIMIT_RETRY_DELAY_MS));
            if (latestQueryRef.current.trim() !== trimmed) return;
            return attempt(retriesLeft - 1);
          }
          if (!res.ok) {
            setSuggestions([]);
            return;
          }
          setSuggestions((await res.json()) as SecurityRef[]);
        } catch {
          if (latestQueryRef.current.trim() === trimmed) setSuggestions([]);
        }
      }

      void attempt(1);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [query]);

  return suggestions;
}
